import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authAPI } from '@/lib/api';
import i18n, { SUPPORTED_LANGUAGES } from '@/i18n';
import clearApiCache from '@/lib/clearApiCache';

// Sync i18next to a freshly-authenticated user's saved language preference,
// so the UI switches without a full reload.
const syncI18nToUser = (userData) => {
  const lang = userData?.language;
  if (lang && SUPPORTED_LANGUAGES.includes(lang) && i18n.language !== lang) {
    i18n.changeLanguage(lang);
  }
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingPhone, setPendingPhone] = useState(null);
  // Backend may return an otp_hint field on register when
  // AUTH_OTP_MODE=mock — a dev/testing convenience so testers can burn
  // through many fake numbers without receiving a real SMS. When
  // present, VerifyOTPPage pre-fills the boxes with this value and
  // shows a small banner explaining why. Never returned in production
  // Twilio mode.
  const [pendingOtpHint, setPendingOtpHint] = useState(null);

  useEffect(() => {
    // Check for existing session
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (storedUser && token) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        syncI18nToUser(parsed);
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  const register = useCallback(async (userData) => {
    // Backend creates the user (with hashed password) and starts a
    // Twilio Verify signup OTP. Frontend routes to /verify-otp next.
    // Response carries otp_hint only when the backend is in mock mode
    // (AUTH_OTP_MODE=mock) — used to pre-fill the OTP boxes for testers.
    const response = await authAPI.register(userData);
    setPendingPhone(userData.phone);
    setPendingOtpHint(response.data?.otp_hint || null);
    return response.data;
  }, []);

  const login = useCallback(async (phone, password, recaptchaToken = null) => {
    // Phone + password login. Backend returns tokens directly — no OTP.
    // recaptchaToken is forwarded when the deploy has RECAPTCHA_ENABLED
    // on; backend silently ignores it otherwise.
    const response = await authAPI.login({
      phone,
      password,
      recaptcha_token: recaptchaToken,
    });
    const { user: userData, access_token } = response.data;

    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', access_token);
    setUser(userData);
    setPendingPhone(null);
    syncI18nToUser(userData);

    return userData;
  }, []);

  const verifySignupOTP = useCallback(async (phone, otp) => {
    // Only used during the signup flow after /auth/register.
    const response = await authAPI.verifySignupOTP({ phone, otp });
    const { user: userData, access_token } = response.data;

    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', access_token);
    setUser(userData);
    setPendingPhone(null);
    syncI18nToUser(userData);

    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    clearApiCache();
    setUser(null);
    setPendingPhone(null);
    setPendingOtpHint(null);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser((current) => {
      const updatedUser = { ...current, ...userData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  // Memoise the context value so consumers only re-render when a member
  // actually changes, not on every parent render.
  const value = useMemo(
    () => ({
      user,
      loading,
      pendingPhone,
      pendingOtpHint,
      isAuthenticated: !!user,
      register,
      login,
      verifySignupOTP,
      logout,
      updateUser,
    }),
    [user, loading, pendingPhone, pendingOtpHint, register, login, verifySignupOTP, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
